import React, { useCallback, useEffect, useRef, useState, Suspense } from 'react';
import { config} from './components/constants';
import './App.scss';
import './styles/teacherView.shell.scss';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Protected from './components/Protected';
import SkillAccessGate from './components/paywall/SkillAccessGate.js';
import PremiumAccessGate from './components/paywall/PremiumAccessGate.js';
import SkillCompleteGate from './components/paywall/SkillCompleteGate.js';
import { PaywallProvider } from './components/paywall/PaywallProvider.js';
import SignInModal from './components/paywall/SignInModal.js';
import JoinCircusModal from './components/paywall/JoinCircusModal.js';
import LearnMore from './components/paywall/LearnMore.js';
import TeacherProtected from './components/TeacherProtected';
import SiteAdminProtected from './components/SiteAdminProtected';
import Navigation from "./components/navbar/navbar.component.navbar";
import ClassInviteBanner from "./components/class/ClassInviteBanner.js";
import Footer from "./components/footer/footer.component.js";
import Splash from './components/splash.component.js';
import LoginWithGoogle from './components/auth/auth.component.loginWithGoogle.js';
import TeacherOnboarding from './components/auth/TeacherOnboarding.js';
import TeacherEmailVerified from './components/auth/TeacherEmailVerified.js';
import TeacherAccessInvite from './components/invite/TeacherAccessInvite.js';
import TeacherAccessWelcomeModal from './components/teacher/TeacherAccessWelcomeModal.js';
import { captureReferralFromSearch } from './utils/referralStorage.js';
import { navigateAfterAuth } from './utils/authRedirect.js';
import useLastSkillTracking from './components/hooks/useLastSkillTracking.js';
import RouteLoadingFallback from './components/infrastructure/RouteLoadingFallback.js';
import AppPageShell from './components/infrastructure/AppPageShell.js';
import TeacherPageShell from './components/infrastructure/TeacherPageShell.js';
import { useBodyThemeSync } from './hooks/useAppTheme.js';
import { isCreamShellRoute } from './themes/bigTopRollout.js';
import { isTeacherViewRoute } from './themes/teacherViewRollout.js';
import './styles/app-theme-big-top.scss';

// Route components — loaded on demand
const RecentActivity = React.lazy(() => import('./components/class/class.component.recentActivity'));
const RecentClassReviewActivity = React.lazy(() => import('./components/class/class.component.recentClassReviewActivity'));
const SkillProgress = React.lazy(() => import('./components/class/class.component.skillProgress'));
const Success = React.lazy(() => import('./components/success.component'));
const ManualLoginForm = React.lazy(() => import('./components/auth/ManualLoginForm.js'));
const RegisterForm = React.lazy(() => import('./components/auth/RegisterForm.js'));
const ForgotPassword = React.lazy(() => import('./components/auth/ForgotPassword.js'));
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword.js'));
const LogOut = React.lazy(() => import('./components/auth/auth.component.logout.js'));
const AccountDeleted = React.lazy(() => import('./components/auth/auth.component.accountDeleted.js'));
const ViewProfile = React.lazy(() => import('./components/userInfo/userInfo.component.viewProfile.js'));
const NewsQuiz = React.lazy(() => import('./components/newsQuiz/NewsQuiz.js'));
const Admin = React.lazy(() => import('./components/admin'));
const CCAdmin = React.lazy(() => import('./components/ccAdmin/ccAdmin.js'));
const CalculusHome = React.lazy(() => import('./components/calculus/calculus.component.calculusHome.js'));
const TutorialTopics = React.lazy(() => import('./components/calculus/calculus.component.tutorialTopics.js'));
const QuizCategories = React.lazy(() => import('./components/quiz/quizCategories.component.quiz'));
const QuizCategoriesButtons = React.lazy(() => import('./components/quiz/quizCategoriesButtons.component.quiz'));
const GetQuestions = React.lazy(() => import('./components/quiz/quizBase.component.quiz'));
const QuizBaseImages = React.lazy(() => import('./components/quiz/quizBaseImages.component.quiz'));
const CurveSketchingIntroTutorial = React.lazy(() => import('./components/curveSketching/curveSketching.component.curveSketchingIntroTutorial.js'));
const QuizTutorial = React.lazy(() => import('./components/quiz/quizTutorial.component.quiz'));
const QuizHumanities = React.lazy(() => import('./components/quiz/quiz.component.humanitiesQuiz'));
const QuizHumanitiesTopics = React.lazy(() => import('./components/quiz/quiz.component.humanitiesQuizTopics'));
const Multiplication = React.lazy(() => import('./components/multiply/multiply.component.multiplication.js'));
const ExponentsVariety = React.lazy(() => import('./components/summerPrep/summerPrep.component.exponentsVariety.js'));
const Derivatives = React.lazy(() => import('./components/derivatives/derivatives.component.derivatives.js'));
const SkillComplete = React.lazy(() => import('./components/status/status.component.skillComplete.js'));
const Markdown = React.lazy(() => import('./components/calculus/calculus.component.markdown'));
const PrivacyPolicy = React.lazy(() => import('./components/info/info.component.privacy.js'));
const TermsOfService = React.lazy(() => import('./components/info/info.component.termsofservice.js'));
const PlainTermsOfService = React.lazy(() => import('./components/info/info.component.plaintermsofservice.js'));
const About = React.lazy(() => import('./components/info/info.component.about.js'));
const Contact = React.lazy(() => import('./components/info/info.component.contact.js'));
const DerivativeRules = React.lazy(() => import('./components/flashcards/flashcards.component.derivativeRules.js'));
const FlippableCard = React.lazy(() => import('./components/flashcards/flashcards.component.gptFlashcard.js'));
const GFGFlippableCard = React.lazy(() => import('./components/flashcards/flashcards.component.gfgFlashcard.js'));
const CardStack = React.lazy(() => import('./components/flashcards/flashcards.component.cardStack.js'));
const CardSlide = React.lazy(() => import('./components/flashcards/flashcards.component.cardSlide.js'));
const UserProgress = React.lazy(() => import('./components/userInfo/userInfo.component.userProgress'));
const UsersCC = React.lazy(() => import('./components/userInfo/userInfo.component.usersCC.js'));
const ManageClasses = React.lazy(() => import('./components/class/class.component.manageClasses.js'));
const CreateClass = React.lazy(() => import('./components/class/class.component.createClass.js'));
const CreateTargets = React.lazy(() => import('./components/class/class.component.createTargets'));
const ClassMidway = React.lazy(() => import('./components/class/class.component.midway.js'));
const PerformanceRace = React.lazy(() => import('./components/performance/performance.component.performanceRace.js'));
const InviterWaiting = React.lazy(() => import('./components/performance/performance.component.inviterWaiting.js'));
const PerformanceResult = React.lazy(() => import('./components/performance/performance.component.performanceResult.js'));
const AddStudentsToClass = React.lazy(() => import('./components/class/class.component.addStudentsToClass.js'));
const PurchasedSeats = React.lazy(() => import('./components/class/class.component.purchasedSeats.js'));
const ClassSeatPricing = React.lazy(() => import('./components/class/class.component.classSeatPricing.js'));
const ClassJoinPage = React.lazy(() => import('./components/class/class.component.joinClass.js'));
const ClassInvitePage = React.lazy(() => import('./components/class/class.component.classInvite.js'));
const EditClass = React.lazy(() => import('./components/class/class.component.editClass.js'));
const ViewClassProgress = React.lazy(() => import('./components/class/class.component.viewClassProgress.js'));
const SkillSelector = React.lazy(() => import('./components/class/class.component.skillSelector.js'));
// const TrigonometricFunctions = React.lazy(() => import('./components/derivatives/derivatives.component.trigonometricFunctions.js'));
const UnitCircle = React.lazy(() => import('./components/summerPrep/summerPrep.component.unitCircle.js'));
const NaturalDerivatives = React.lazy(() => import('./components/derivatives/derivatives.component.naturalDerivatives.js'));
const Integration = React.lazy(() => import('./components/integration/integration.component.integration'));
const IntegrationAccumulation = React.lazy(() => import('./components/integration/integration.component.integrationAccumulation'));
const SeparationOfVariables = React.lazy(() => import('./components/differentialEquations/differentialEquations.component.separationOfVariables'));
const ReviewCardSelection = React.lazy(() => import('./components/review/review.component.reviewCardSelection.js'));
const ReviewCards = React.lazy(() => import('./components/review/review.component.reviewCards.js'));
const ReviewCompletion = React.lazy(() => import('./components/review/review.component.reviewCompletion.js'));
const ReviewSessionManager = React.lazy(() => import('./components/review/review.component.reviewSessionManager.js'));
const DerivativeRulesStandards = React.lazy(() => import('./components/standards/derivativeRulesStandards.component.standards.js'));
const StandardsCategories = React.lazy(() => import('./components/standards/standardsCategories.component.standards'));
const StandardsTopics = React.lazy(() => import('./components/standards/standardsTopics.component.standards'));
const StandardsTracker = React.lazy(() => import('./components/standards/standardsTracker.component.standards'));
const CubesAndSquares = React.lazy(() => import('./components/summerPrep/summerPrep.component.cubesAndSquares.js'));
const IdentifyingFunctions = React.lazy(() => import('./components/summerPrep/summerPrep.component.identifyingFunctions.js'));
const AverageRateOfChange = React.lazy(() => import('./components/summerPrep/summerPrep.component.averageRateOfChange.js'));
const PlottingPointsSkill = React.lazy(() => import('./components/summerPrep/summerPrep.component.plottingPointsSkill.js'));
const GraphingFunctions = React.lazy(() => import('./components/summerPrep/summerPrep.component.graphingFunctions.js'));
const FactoringQuadratics = React.lazy(() => import('./components/summerPrep/summerPrep.component.factoringQuadratics.js'));
const SVGGraphs = React.lazy(() => import('./components/summerPrep/svgGraphs.component.summerPrep.js'));
const EquationsOfLines = React.lazy(() => import('./components/summerPrep/summerPrep.component.equationsOfLines.js'));
const LimitsLanguage = React.lazy(() => import('./components/limits/limits.component.limitsLanguage.js'));
const LimitsGraphs = React.lazy(() => import('./components/limits/limits.component.limitsGraphs.js'));
const LimitsTables = React.lazy(() => import('./components/limits/limits.component.limitsTables.js'));
const LimitsExpressions = React.lazy(() => import('./components/limits/limits.component.limitsExpressions.js'));
const LimitsMix = React.lazy(() => import('./components/limits/limits.component.limitsMix.js'));
const LimitsContinuity = React.lazy(() => import('./components/limits/limits.component.limitsContinuity.js'));
const LimitsRemovable = React.lazy(() => import('./components/limits/limits.component.limitsRemovable.js'));
const LimitsIntervalContinuity = React.lazy(() => import('./components/limits/limits.component.limitsIntervalContinuity.js'));
const LimitsIVT = React.lazy(() => import('./components/limits/limits.component.limitsIVT.js'));
const LimitsProperties = React.lazy(() => import('./components/limits/limits.component.limitsProperties.js'));
const LimitsSqueeze = React.lazy(() => import('./components/limits/limits.component.limitsSqueeze.js'));
const LimitsProcedureMix = React.lazy(() => import('./components/limits/limits.component.limitsProcedureMix.js'));
const LimitsDividingOut = React.lazy(() => import('./components/limits/limits.component.limitsDividingOut.js'));
const ProgressStreak = React.lazy(() => import('./components/status/status.component.progressStreak.js'));
const ProgressFeedback = React.lazy(() => import('./components/status/status.component.progressFeedback.js'));
const SectionHome = React.lazy(() => import('./components/pathways/sectionHome.component.js'));
const CourseHome = React.lazy(() => import('./components/pathways/courseHome.component.js'));
const SkillsHome = React.lazy(() => import('./components/pathways/skillsHome.component.js'));
const TopicsHome = React.lazy(() => import('./components/pathways/topicsHome.component.js'));
const SubskillsHome = React.lazy(() => import('./components/pathways/subskillsHome.component.js'));
const SkillFrame = React.lazy(() => import('./components/pathways/skillFrame.component.js'));
const MyComponent = React.lazy(() => import('./components/sandbox/fetch.component'));
const Dropdown = React.lazy(() => import('./components/sandbox/dropdown'));
const Create = React.lazy(() => import('./components/sandbox/create.component'));
const QuestionArea = React.lazy(() => import('./components/sandbox/question-area.component'));
const GetAPI = React.lazy(() => import('./components/sandbox/getAPI.component'));
const GetAPIWithEffect = React.lazy(() => import('./components/sandbox/getAPIWithEffect.component'));
const GetAPIAxios = React.lazy(() => import('./components/sandbox/getAPIAxios.component'));
const GetTrivia = React.lazy(() => import('./components/sandbox/getTrivia.component'));
const SvgSandbox = React.lazy(() => import('./components/sandbox/svgSandbox'));
const DerivativeVisualizer = React.lazy(() => import('./components/p5jsComponents/derivativeVisualizer'));
const MeanValueTheoremVisualizer = React.lazy(() => import('./components/p5jsComponents/meanValueTheoremVisualizer'));

// Wrapper component to pass location state to ProgressStreak
const ProgressStreakWrapper = () => {
  const location = useLocation();
  const completedSkillInfo = location.state?.completedSkillInfo;
  return <ProgressStreak completedSkillInfo={completedSkillInfo} />;
};

const url = config.url.API_URL;

const MANUAL_AUTH_ROUTES = ['/login/email', '/register', '/forgot-password', '/reset-password'];
const AUTH_PAGES = ['/', '/loginWithGoogle', '/login', '/logout', ...MANUAL_AUTH_ROUTES];
const HIDE_CHROME_ROUTES = ['/', '/loginWithGoogle', '/logout', ...MANUAL_AUTH_ROUTES];

// Custom hook for automatic token refresh
const useAuthRefresh = (isAuthenticated, setIsAuthenticated, setUserId, navigate) => {
  const location = useLocation();
  
  useEffect(() => {
    // Don't start refresh on login/register pages or if not authenticated
    const authPages = AUTH_PAGES;
    if (!isAuthenticated || authPages.includes(location.pathname)) {
      return;
    }

    let failureCount = 0;
    const maxFailures = 3;
    const controller = new AbortController();
    
    const checkAndRefreshAuth = async () => {
      try {
        const response = await fetch(`${url}/record/checkAuth`, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal,
        });
        const data = await response.json();
        
        if (data.authenticated) {
          if (data.refreshed) {
          }
          failureCount = 0; // Reset failure count on success
        } else {
          failureCount++;
          console.warn(`Auth check failed (attempt ${failureCount}/${maxFailures})`);
          
          if (failureCount >= maxFailures) {
            console.error('Max auth failures reached, redirecting to login');
            setIsAuthenticated(false);
            setUserId("");
            navigate("/loginWithGoogle", { 
              state: { 
                from: location.pathname,
                message: "Your session has expired. Please log in again." 
              }
            });
            return;
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        failureCount++;
        console.error(`Auth refresh error (attempt ${failureCount}/${maxFailures}):`, error);
        
        if (failureCount >= maxFailures) {
          console.error('Max auth failures reached, redirecting to login');
          setIsAuthenticated(false);
          setUserId("");
          navigate("/loginWithGoogle", { 
            state: { 
              from: location.pathname,
              message: "Connection error. Please log in again." 
            }
          });
        }
      }
    };

    // Set up interval for periodic auth checks (1 hour = 3600000 ms)
    const refreshInterval = setInterval(checkAndRefreshAuth, 3600000);
    
    // Initial immediate check
    checkAndRefreshAuth();

    // Cleanup interval on unmount or dependency change
    return () => {
      controller.abort();
      clearInterval(refreshInterval);
    };
  }, [isAuthenticated, location.pathname, setIsAuthenticated, setUserId, navigate]);
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authChecked, setAuthChecked] = useState(false);
  const [initialAccess, setInitialAccess] = useState(null);
  const [userId, setUserId] = useState("");
  const [username, setUsername] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [userRoles, setUserRoles] = useState(null);
  const [needsTeacherOnboarding, setNeedsTeacherOnboarding] = useState(false);
  const [teacherAccessWelcomeOpen, setTeacherAccessWelcomeOpen] = useState(false);
  const returningUserRedirectAttempted = useRef(false);
  const { getLastSkill, findNextLearningOpportunity } = useLastSkillTracking(null);
  
  const handleSessionRefresh = useCallback(({ userRoles: roles, access, showTeacherAccessWelcome } = {}) => {
    if (roles) {
      setUserRoles(roles);
    }
    if (access) {
      setInitialAccess(access);
    }
    if (showTeacherAccessWelcome) {
      setTeacherAccessWelcomeOpen(true);
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Set up automatic token refresh
  useAuthRefresh(isAuthenticated, setIsAuthenticated, setUserId, navigate);
  useEffect(() => {
    const fetchUserId = async () => {    
      try {
        const result = await fetch(`${url}/record/checkAuth`, {
          method: 'GET',
          credentials: 'include',
        })
        const answer = await result.json();
        if (answer.authenticated) {

          setIsAuthenticated(true);
          setUserId(answer.userId);
          setNeedsTeacherOnboarding(Boolean(answer.needsTeacherOnboarding));
          if (answer.access) {
            setInitialAccess(answer.access);
          }
        } else {
          setIsAuthenticated(false);
          setNeedsTeacherOnboarding(false);
          setInitialAccess(null);
          // navigate("/loginWithGoogle");
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        setIsAuthenticated(false);
        // navigate("/loginWithGoogle");
      } finally {
        setAuthChecked(true);
      }
    }

    fetchUserId();
  }, [navigate]);

  useEffect(() => {
    if (
      !authChecked ||
      !userId ||
      location.pathname !== '/' ||
      returningUserRedirectAttempted.current
    ) {
      return undefined;
    }

    returningUserRedirectAttempted.current = true;
    navigateAfterAuth({
      navigate,
      from: null,
      userId,
      getLastSkill,
      findNextLearningOpportunity,
      needsTeacherOnboarding,
    });

    return undefined;
  }, [
    authChecked,
    findNextLearningOpportunity,
    getLastSkill,
    location.pathname,
    navigate,
    needsTeacherOnboarding,
    userId,
  ]);

  useEffect(() => {
    if (!userId || AUTH_PAGES.includes(location.pathname)) {
      return undefined;
    }

    const controller = new AbortController();
    setUserRoles(null);

    const fetchData = async () => {
      try {
        const response = await fetch(`${url}/users/fetchUsername?userId=${encodeURIComponent(userId)}`, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal,
        });
        const data = await response.json();
        if (data.username) {
          setUsername(data.username);
          setAvatarUrl(data.avatar);
        }
        setUserRoles(data.userRoles || {});
        if (data.showTeacherAccessWelcome) {
          setTeacherAccessWelcomeOpen(true);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Error checking authentication:', error);
        setUserRoles({});
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [userId, location.pathname]);

  const hideNavOnRoutes = HIDE_CHROME_ROUTES;
  const hideFooterOnRoutes = HIDE_CHROME_ROUTES;
  const teacherView = isTeacherViewRoute(location.pathname);
  const creamShell = isCreamShellRoute(location.pathname);

  useEffect(() => {
    captureReferralFromSearch(location.search);
  }, [location.search]);

  useBodyThemeSync();

  // this is added to make login with google work
  const [userEmail, setUserEmail] = useState();
  // if (loading) {
  //   return <div>Loading . . . </div>
  // }
  return (
    <PaywallProvider authChecked={authChecked} isAuthenticated={isAuthenticated} initialAccess={initialAccess}>
    <div className="App text-center">
      <TeacherAccessWelcomeModal
        show={teacherAccessWelcomeOpen}
        onHide={() => setTeacherAccessWelcomeOpen(false)}
      />
      {!hideNavOnRoutes.includes(location.pathname) && (
        <>
        <Navigation 
          isAuthenticated={isAuthenticated}
          username={username}
          avatarUrl={avatarUrl}
          userRoles={userRoles}
          userId={userId}
        />
        <ClassInviteBanner isAuthenticated={isAuthenticated} />
        </>
      )}
      <div className={`appContent container-fluid m-0 p-0${teacherView ? ' appContent--teacher-view' : ''}${creamShell ? ' appContent--cream-shell' : ''}`}>
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route exact path="/" element={<Splash />} />
          <Route path="/success" element={<Success />} />
          <Route path="/news" element={<NewsQuiz />} />
          <Route path="/admin" element={
            <AppPageShell pageKey="teacherAdmin" className="quiz-admin--big-top">
              <Admin />
            </AppPageShell>
          } />
          <Route path="/ccAdmin" element={
            <SiteAdminProtected 
              isAuthenticated={isAuthenticated} 
              authChecked={authChecked}
              userRoles={userRoles}
            >
              <AppPageShell pageKey="siteAdmin">
                <CCAdmin />
              </AppPageShell>
            </SiteAdminProtected>
          } />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/termsofservice" element={<TermsOfService />} />
          <Route path="/plaintermsofservice" element={<PlainTermsOfService />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
          <Route path="/learn-more" element={<LearnMore isAuthenticated={isAuthenticated} />} />
          <Route path="/calculusHome" element={<CalculusHome
                                                  isAuthenticated={isAuthenticated}
                                                  userId={userId}
                                              />}
          />
          <Route path="/courseHome" element={<CourseHome />} />
          <Route path="/sectionHome" element={<SectionHome userId={userId} isAuthenticated={isAuthenticated} />} />
          <Route path="/topicsHome/:unit" element={<TopicsHome userId={userId} isAuthenticated={isAuthenticated} />} />
          <Route path="/skillsHome/:unit/:topic" element={<SkillsHome userId={userId} isAuthenticated={isAuthenticated} />} />
          <Route path="/subskillsHome/:unit/:topic/:skill" element={<SubskillsHome userId={userId} isAuthenticated={isAuthenticated} />} />
          <Route path="/skillFrame/:skillId" element={<SkillFrame isAuthenticated={isAuthenticated} />} />

          <Route path="/login" element={<Navigate to="/loginWithGoogle" replace />} />
          <Route path="/login/email"
                element={
                  <ManualLoginForm
                    setUserEmail={setUserEmail}
                    setIsAuthenticated={setIsAuthenticated}
                    setUserId={setUserId}
                  />
                }
          />
          <Route path="/register"
                element={
                  <RegisterForm
                    setUserEmail={setUserEmail}
                    setIsAuthenticated={setIsAuthenticated}
                    setUserId={setUserId}
                  />
                }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/logout" element={<LogOut
                                            authChecked={authChecked}
                                            setIsAuthenticated={setIsAuthenticated}
                                            setUserId={setUserId}
                                            setUsername={setUsername}
                                            setAvatarUrl={setAvatarUrl}
                                            setUserRoles={setUserRoles}
                                        /> } />
          <Route path="/account-deleted" element={
            <AccountDeleted
              setIsAuthenticated={setIsAuthenticated}
              setUserId={setUserId}
              setUsername={setUsername}
              setAvatarUrl={setAvatarUrl}
              setUserRoles={setUserRoles}
            />
          } />
          
          <Route path="/categories"
                element={
                  <Protected isAuthenticated={isAuthenticated}
                    authChecked={authChecked}>
                    <QuizCategories />
                  </Protected>
                }
          />
          <Route path="/categoriesButtons" element={<QuizCategoriesButtons /> } />
          <Route path="/getQuestions/:categoryId" element={<GetQuestions /> } />
          <Route path="/quizBaseImages" element={<QuizBaseImages /> } />
          {/* <Route path="/quizTutorial" element={<QuizTutorial /> } /> */}

          <Route path="/quizHumanities/:categoryId" element={<QuizHumanities /> } />
          <Route path="/quizHumanitiesTopics" element={<QuizHumanitiesTopics /> } />
          <Route path="/mocktrialquiz" element={<QuizHumanities /> } />
          
          <Route path="/fetch" element={<MyComponent />} />
          <Route path="/dropdown" element={<Dropdown /> } />
          <Route path="/create" element={<Create />} />
          <Route path="/question" element={<QuestionArea />} />
          <Route path="/api" element={<GetAPI />} />
          <Route path="/apiRef" element={<GetAPIWithEffect /> } />
          <Route path="/apiAxios" element={<GetAPIAxios /> } />
          <Route path="/getTrivia" element={<GetTrivia /> } />
          <Route path="/svgSandbox" element={<SvgSandbox />} />
          <Route path="/derivativeVisualizer" element={<DerivativeVisualizer />} />
          <Route path="/meanValueTheoremVisualizer" element={<MeanValueTheoremVisualizer />} />

          <Route path="/viewProfile"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <ViewProfile userId={userId} />
                  </Protected>
                }
          />

          <Route path="/multiply"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId} defaultSkillId="10">
                    <Multiplication />
                  </SkillAccessGate>
                }
          />

          <Route path="/exponentsVariety"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <ExponentsVariety userId={userId}/>
                  </SkillAccessGate>
                }
          />                    
          <Route path="/derivatives"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <Derivatives userId={userId}/>
                  </SkillAccessGate>
                }
          />

          <Route path="/productQuotientRule"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <Derivatives userId={userId}/>
                  </SkillAccessGate>
                }
          />

          <Route path="/curveSketchingIntroTutorial"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <CurveSketchingIntroTutorial userId={userId}/>
                  </SkillAccessGate>
                }
          />          

          <Route path="/skillComplete"
                element={
                  <SkillCompleteGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId}>
                    <SkillComplete userId={userId} />
                  </SkillCompleteGate>
                }
          />

          <Route path="/markdown"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <Markdown userId={userId}/>
                  </Protected>
                }
          />    
          <Route path="/usersCC"
                element={
                  <Protected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}>
                      <UsersCC userId={userId}/>
                  </Protected>
                }
          />
          <Route path="/userProgress/:userId"
                element={
                  <Protected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}>
                      <UserProgress/>
                  </Protected>
                }
          />
          <Route path="/manageClasses"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <ManageClasses userId={userId} username={username}/>
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />                     
          <Route path="/createClass"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <CreateClass userId={userId} username={username}/>
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/addStudentsToClass/:classCode"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <AddStudentsToClass userId={userId}/>
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/purchasedSeats"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <PurchasedSeats />
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/classSeatPricing"
                element={
                  <TeacherPageShell>
                    <ClassSeatPricing isTeacher={Boolean(userRoles?.teacher)} />
                  </TeacherPageShell>
                }
          />
          <Route path="/recentClassActivity/:classCode"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <RecentActivity />
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/recentClassReviewActivity/:classCode"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <RecentClassReviewActivity />
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/skillSelector/:classCode"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <SkillSelector />
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/skillProgress/:classCode/:skillId"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <SkillProgress />
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/editClass/:classCode"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <EditClass userId={userId}/>
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/viewClassProgress/:classCode"
                element={
                  <TeacherProtected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userRoles={userRoles}>
                      <TeacherPageShell>
                        <ViewClassProgress userId={userId}/>
                      </TeacherPageShell>
                  </TeacherProtected>
                }
          />
          <Route path="/createTargets"
                element={
                  <Protected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}>
                      <CreateTargets userId={userId}/>
                  </Protected>
                }
          />
          {/* <Route path="/trigonometricFunctions/:topic"
                element={
                  <Protected
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}>
                      <TrigonometricFunctions userId={userId} />
                  </Protected>
                } 
          />                                                                                                             */}
          <Route path="/trigonometricDerivatives"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                        <Derivatives userId={userId} />
                    </SkillAccessGate>
                  } 
          />

          <Route path="/unitCircle"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <UnitCircle userId={userId} />
                  </SkillAccessGate>
                } 
          />

          <Route path="/naturalDerivatives"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                        <Derivatives userId={userId} />
                    </SkillAccessGate>
                  } 
          />
          <Route path="/integration"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <Integration userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/integrationAccumulation"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <IntegrationAccumulation userId={userId}/>
                  </SkillAccessGate>
                }
          />                                                  
          <Route path="/separationOfVariables"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <SeparationOfVariables userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/reviewCards/sessions"
                element={
                  <PremiumAccessGate
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userId={userId}
                    feature="review-cards"
                  >
                    <ReviewSessionManager userId={userId} />
                  </PremiumAccessGate>
                }
          />
          <Route path="/reviewCards"
                element={
                  <PremiumAccessGate
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userId={userId}
                    feature="review-cards"
                  >
                    <ReviewCardSelection userId={userId} />
                  </PremiumAccessGate>
                }
          />
          <Route path="/reviewCards/session"
                element={
                  <PremiumAccessGate
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userId={userId}
                    feature="review-cards"
                  >
                    <ReviewCards userId={userId} />
                  </PremiumAccessGate>
                }
          />
          <Route path="/reviewCards/completion"
                element={
                  <PremiumAccessGate
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userId={userId}
                    feature="review-cards"
                  >
                    <ReviewCompletion userId={userId} />
                  </PremiumAccessGate>
                }
          />
          <Route path="/derivativeRules"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <DerivativeRules userId={userId} />
                    </SkillAccessGate>
                  } 
          />
          <Route path="/gptFlashcard"
                  element={
                    <Protected 
                      isAuthenticated={isAuthenticated}
                      authChecked={authChecked}
                    >
                      <FlippableCard userId={userId} />
                    </Protected>
                  } 
          />
          <Route path="/gfgFlashcard"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <GFGFlippableCard userId={userId} />
                    </SkillAccessGate>
                  } 
          /> 
          <Route path="/cardStack"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <CardStack userId={userId} />
                    </SkillAccessGate>
                  } 
          /> 
          <Route path="/cardSlide"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                      <CardSlide userId={userId} />
                    </SkillAccessGate>
                  } 
          /> 
          <Route path="/quizTutorial"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                        <QuizTutorial userId={userId} />
                    </SkillAccessGate>
                  }
          />
          <Route path="/tutorialTopics"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                        <TutorialTopics userId={userId} />
                    </SkillAccessGate>
                  }
          />                              
          <Route path="/derivativeRulesStandards"
                  element={
                    <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                        <DerivativeRulesStandards userId={userId} />
                    </SkillAccessGate>
                  }
          />                                        
          <Route path="/standardsCategories"
                  element={
                    <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                        <StandardsCategories userId={userId} />
                    </Protected>
                  }
          />
          <Route path="/standardsTopics/:category"
                  element={
                    <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                      <StandardsTopics userId={userId} />
                   </Protected>
                  }
          />                                                                                                                                                                       
          <Route path="/standardsTracker/:categoryWord/:topic"
                  element={
                    <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                      <StandardsTracker userId={userId} />
                   </Protected>
                  }
          />
          <Route path="/cubesAndSquares/:topic"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <CubesAndSquares userId={userId}/>
                  </SkillAccessGate>
                }
          />            
          <Route path="/identifyingFunctions"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <IdentifyingFunctions userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/averageRateOfChange"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId} defaultSkillId="2000">
                    <AverageRateOfChange userId={userId}/>
                  </SkillAccessGate>
                }
          />           
          <Route path="/plottingPoints"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId} defaultSkillId="120">
                    <PlottingPointsSkill userId={userId} defaultSkillId="120" />
                  </SkillAccessGate>
                }
          />                  

         <Route path="/plottingPointsPolynomials"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId} defaultSkillId="110">
                    <PlottingPointsSkill userId={userId} defaultSkillId="110" />
                  </SkillAccessGate>
                }
          />                                                                                                                                                                                                                                                                                                                                                     

          <Route path="/graphingFunctions"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <GraphingFunctions userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/factoringQuadratics/:topic"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <FactoringQuadratics userId={userId}/>
                  </SkillAccessGate>
                }
          />

          <Route path="/svgGraphs"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <SVGGraphs userId={userId}/>
                  </SkillAccessGate>
                }
          />                                                                                                                                                                             
          <Route path="/functionNotation"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId} defaultSkillId="100">
                    <PlottingPointsSkill userId={userId} defaultSkillId="100" />
                  </SkillAccessGate>
                }
          />
          <Route path="/equationsOfLines"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <EquationsOfLines userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsLanguage"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsLanguage userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsGraphs"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsGraphs userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsTables"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsTables userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsExpressions"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsExpressions userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsMix"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsMix userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsContinuity"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsContinuity userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsRemovable"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsRemovable userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsIntervalContinuity"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsIntervalContinuity userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsProperties"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsProperties userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsIVT"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsIVT userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsSqueeze"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsSqueeze userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsProcedureMix"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsProcedureMix userId={userId}/>
                  </SkillAccessGate>
                }
          />
          <Route path="/limitsDividingOut"
                element={
                  <SkillAccessGate isAuthenticated={isAuthenticated} authChecked={authChecked} userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId}>
                    <LimitsDividingOut userId={userId}/>
                  </SkillAccessGate>
                }
          />          
          <Route path="/progressStreak"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <ProgressStreakWrapper />
                  </Protected>
                }
          />
          <Route path="/progressFeedback"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <ProgressFeedback />
                  </Protected>
                }
          />
          <Route path="/midway"
                element={
                  <PremiumAccessGate
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userId={userId}
                    feature="midway"
                  >
                    <ClassMidway userId={userId} />
                  </PremiumAccessGate>
                }
          />
          <Route path="/midway/:classCode"
                element={
                  <PremiumAccessGate
                    isAuthenticated={isAuthenticated}
                    authChecked={authChecked}
                    userId={userId}
                    feature="midway"
                  >
                    <ClassMidway userId={userId} />
                  </PremiumAccessGate>
                }
          />
          <Route path="/performance/:performanceId/race"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <PerformanceRace userId={userId} />
                  </Protected>
                }
          />
          <Route path="/performance/:performanceId/waiting"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <InviterWaiting userId={userId} />
                  </Protected>
                }
          />
          <Route path="/performance/:performanceId/result"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <PerformanceResult userId={userId} />
                  </Protected>
                }
          />                                                                                                                                                       
          <Route path="/loginWithGoogle"
                element={
                    <LoginWithGoogle
                      setUserEmail={setUserEmail}
                      setIsAuthenticated={setIsAuthenticated}
                      setUserId={setUserId}
                    />
                }
          />
          <Route path="/onboarding/teacher"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <TeacherOnboarding userId={userId} />
                  </Protected>
                }
          />
          <Route path="/teacher-request/verified" element={<TeacherEmailVerified />} />
          <Route path="/teacher-access/:code" element={<TeacherAccessInvite onSessionRefresh={handleSessionRefresh} />} />
          <Route path="/join/:classCode"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <ClassJoinPage userId={userId} isAuthenticated={isAuthenticated} />
                  </Protected>
                }
          />
          <Route path="/class-invite/:inviteId"
                element={
                  <Protected isAuthenticated={isAuthenticated} authChecked={authChecked}>
                    <ClassInvitePage />
                  </Protected>
                }
          />
        </Routes>
        </Suspense>
      </div>
      {!hideFooterOnRoutes.includes(location.pathname) && <Footer />}
      <SignInModal
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        setUserId={setUserId}
      />
      <JoinCircusModal />
    </div>
    </PaywallProvider>
  );
}

export default App;
